import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import apiClient from "../../../../lib/api_client";

export const AI_ID_PREFIX = "ai:";
export const aiCollectionId = (name) => `${AI_ID_PREFIX}${name}`;
export const isAICollectionId = (id) => String(id || "").startsWith(AI_ID_PREFIX);

const PAGE_SIZE = 50;

// Body for APIs 2 and 3: the collection item from API 1 carries the flags
// (days, newDesign, cadDesign, ...) that tell the backend which collection it is.
const collectionFlags = (collection) => {
  if (!collection) return {};
  const { designCount, _id, isAI, ...flags } = collection;
  return flags;
};

const payloadOf = (res) => res?.data?.data ?? res?.data ?? {};

// --------------------------------------------- 1. collections list ---------------------------------------------

const fetchAICollections = async () => {
  const res = await apiClient.post("/getAICollections", {}).catch((err) => {
    console.error("getAICollections failed", err?.response?.status, err?.response?.data);
    throw err;
  });
  const d = payloadOf(res);
  const list = Array.isArray(d)
    ? d
    : d?.designs || d?.collections || d?.data || d?.list || [];
  if (!Array.isArray(list) || list.length === 0) {
    console.warn("getAICollections returned no collections", res?.data);
  }
  return (Array.isArray(list) ? list : []).map((c) => ({
    ...c,
    _id: aiCollectionId(c.collectionName),
    isAI: true,
  }));
};

export const useAICollections = () =>
  useQuery({
    queryKey: ["aiCollections"],
    queryFn: fetchAICollections,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

// --------------------------------------------- 2. categories in a collection -----------------------------------

const fetchAICategories = async (collection) => {
  const res = await apiClient.post("/getSpecificAICollectionData", {
    ...collectionFlags(collection),
    collectionName: collection.collectionName,
    searchTerm: "",
  });
  const categories = payloadOf(res)?.categories;
  return (Array.isArray(categories) ? categories : []).map((c) => ({
    name: c.category,
    count: c.count,
  }));
};

export const useAICategories = (collection) =>
  useQuery({
    queryKey: ["aiCollectionCategories", collection?.collectionName],
    queryFn: () => fetchAICategories(collection),
    enabled: !!collection?.isAI,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

// --------------------------------------------- 3. designs (paginated) ------------------------------------------

// The response is described only as "same shape as Vision", so be tolerant
// about where the list and the paging info sit.
const normalisePage = (res, pageNumber) => {
  const d = payloadOf(res);
  const designs = Array.isArray(d)
    ? d
    : d?.designs || d?.data || d?.designList || [];
  const totalPages = d?.totalPages ?? d?.pagination?.totalPages;
  const hasMore =
    totalPages != null ? pageNumber < totalPages : designs.length >= PAGE_SIZE;
  return { designs, hasMore };
};

const fetchAIDesigns = async ({ collection, categoryName, pageNumber }) => {
  const res = await apiClient.post("/getSpecificAICollectionDesigns", {
    ...collectionFlags(collection),
    collectionName: collection.collectionName,
    categoryName,
    search: "",
    pageNumber,
    limit: PAGE_SIZE,
  });
  return normalisePage(res, pageNumber);
};

export const useAIDesigns = (collection, categoryName) =>
  useInfiniteQuery({
    queryKey: ["aiCollectionDesigns", collection?.collectionName, categoryName],
    queryFn: ({ pageParam = 1 }) =>
      fetchAIDesigns({ collection, categoryName, pageNumber: pageParam }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
    enabled: !!collection?.isAI && !!categoryName,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
